import React from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat, Calendar, AlarmClock } from 'lucide-react';
import { calculateTaskGanttPos, getStageColor, assignGanttTracks } from '../lib/taskGrid';

// slice 02.1 · paso b.4 — el timeline "hoy" de la pantalla de tareas (barras agrupadas por
// encargado sobre el eje de horas). Se muestra sólo con `timeRange==='hoy' && activeTab==='lista'`.
// Presentacional: recibe `ganttTasks` + el config del Gantt + los filtros ya resueltos + los
// `on*` de drag/resize.
export const TaskGanttView = ({
    containerRef, ganttConfig, ganttTasks, loading, startStr, userTz, systemUsers = [],
    taskFilterUsers, activeSpaceId, spaceMembersData = [],
    dragOverLeft, currentTimeOffset, isResizing,
    onHeaderDragOver, onHeaderDrop, onRowDragOver, onRowDrop,
    onBarDragStart, onBarDragEnd, onResizeStart, onResizeMove, onResizeEnd, onBarClick,
}) => {
    const { t: translate } = useTranslation('tasks');

    const rows = (() => {
        const grouped = {};
        ganttTasks.forEach(task => {
            const assignees = (task.assignee || 'Sin Asignar').split(',').map(s => s.trim()).filter(Boolean);
            if (assignees.length === 0) assignees.push('Sin Asignar');
            assignees.forEach(assignee => {
                if (!grouped[assignee]) grouped[assignee] = [];
                grouped[assignee].push(task);
            });
        });

        let ganttRows = Object.entries(grouped);

        if (taskFilterUsers && taskFilterUsers.length > 0) {
            ganttRows = ganttRows.filter(([assigneeName]) => {
                if (assigneeName === 'Sin Asignar') return false;
                return taskFilterUsers.some(u => u.nick && u.nick.toLowerCase() === assigneeName.toLowerCase());
            });
        }

        // Filtro de miembros del espacio: no mostrar filas de usuarios que no pertenecen al espacio
        if (activeSpaceId && spaceMembersData.length > 0) {
            ganttRows = ganttRows.filter(([assigneeName]) => {
                if (assigneeName === 'Sin Asignar') return true;
                return spaceMembersData.some(m => (m.nick || m.name).toLowerCase() === assigneeName.toLowerCase());
            });
        }
        return ganttRows;
    })();

    return (
        <div className="gantt-section">
            <div className="gantt-timeline-container" ref={containerRef}>
                <div className="gantt-grid">
                    {/* Cabecera de Horas */}
                    <div className="gantt-time-header-row">
                        <div className="gantt-time-header-sticky"></div>
                        {ganttConfig.headers.map((hdr, idx) => (
                            <div
                                key={idx}
                                className="gantt-time-cell"
                                style={{ minWidth: `${hdr.width}px`, width: `${hdr.width}px` }}
                                onDragOver={(e) => onHeaderDragOver(e, idx)}
                                onDrop={(e) => onHeaderDrop(e, idx)}
                            >
                                {hdr.label}
                            </div>
                        ))}
                    </div>

                    {/* Fila del Gantt agrupada por usuario */}
                    {loading ? (
                        <div className="gantt-loading">{translate('empty.loadingTimeline')}</div>
                    ) : (
                        rows.map(([assigneeName, tasksForUser]) => {
                            const { taskTrackMap, totalTracks } = assignGanttTracks(tasksForUser, ganttConfig, startStr, userTz);
                            const rowHeight = Math.max(1, totalTracks) * 34 + 8;

                            const foundUser = systemUsers.find(u => u.nick.toLowerCase() === assigneeName.toLowerCase());
                            const avatarUrl = (foundUser?.['link-profile-img-thumb'] || foundUser?.['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${assigneeName}`;

                            return (
                                <div key={assigneeName} className="gantt-user-group-row">
                                    <div className="gantt-user-sticky-col">
                                        <img src={avatarUrl} alt={assigneeName} className="gantt-user-avatar" loading="lazy" />
                                        <span className="gantt-user-name" title={assigneeName === 'Sin Asignar' ? translate('common.unassigned') : assigneeName}>
                                            {assigneeName === 'Sin Asignar' ? translate('common.notAvailable') : assigneeName}
                                        </span>
                                    </div>
                                    <div
                                        className="gantt-bars-row gantt-user-bars-container"
                                        onDragOver={onRowDragOver}
                                        onDrop={onRowDrop}
                                        style={{ height: `${rowHeight}px` }}
                                    >
                                        {/* Líneas guía verticales del grid */}
                                        {Array.from({ length: ganttConfig.totalSubCols }).map((_, idx) => (
                                            <div key={idx} className="gantt-grid-line" style={{ left: `${idx * ganttConfig.subColWidth}px` }} />
                                        ))}

                                        {/* Indicador azul guía de arrastre (snap de 5 min) */}
                                        {dragOverLeft !== null && (
                                            <div className="timeline-drop-line" style={{ left: `${dragOverLeft}px` }} />
                                        )}

                                        {/* Línea blanca de la hora actual */}
                                        {currentTimeOffset !== null && (
                                            <div className="timeline-current-time-line" style={{ left: `${currentTimeOffset}px` }} title={translate('common.currentTime')} />
                                        )}

                                        {/* Barras del Gantt */}
                                        {tasksForUser.map(task => {
                                            const { startPos, widthSize } = calculateTaskGanttPos(task, ganttConfig, startStr, userTz);
                                            const trackIndex = taskTrackMap[task.id] || 0;
                                            const topPos = trackIndex * 34 + 6;

                                            let bgColor = getStageColor(task.stage);
                                            let borderColor = 'rgba(255,255,255,0.1)';
                                            const isPersonalEvent = task.stage === 'event-black';
                                            const isYellowEvent = task.is_calendar_event && !isPersonalEvent;
                                            if (isPersonalEvent) {
                                                bgColor = '#000000';
                                                borderColor = '#ffffff';
                                            } else if (isYellowEvent) {
                                                bgColor = '#eab308';
                                            }

                                            const isTask = !isPersonalEvent && !isYellowEvent && task.stage !== 'recordatorio';
                                            const bgOpacity = isTask ? 0.35 : 1;

                                            const assigneeStr = typeof task.assignee === 'string' ? task.assignee : (task.assignee?.nick || task.assignee?.name || assigneeName);
                                            const taskUser = systemUsers.find(u => u.nick.toLowerCase() === assigneeStr.toLowerCase());
                                            const inlineAvatarUrl = (taskUser?.['link-profile-img-thumb'] || taskUser?.['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${assigneeStr}`;

                                            return (
                                                <div
                                                    key={task.id}
                                                    data-task-id={task.id}
                                                    draggable={!isResizing && !task.is_invited}
                                                    onDragStart={(e) => onBarDragStart(e, task.id)}
                                                    onDragEnd={onBarDragEnd}
                                                    className="gantt-bar-tag"
                                                    style={{
                                                        left: `${startPos}px`,
                                                        width: `${widthSize}px`,
                                                        top: `${topPos}px`,
                                                        cursor: isResizing ? 'ew-resize' : 'grab',
                                                        background: 'transparent',
                                                        border: 'none',
                                                        boxShadow: 'none',
                                                        padding: 0,
                                                        overflow: 'visible'
                                                    }}
                                                    onClick={(e) => onBarClick(task, e)}
                                                >
                                                    {/* Fondo y Borde */}
                                                    <div style={{
                                                        position: 'absolute', left: 0, top: 0, bottom: 0, width: '100%',
                                                        backgroundColor: bgColor, borderRadius: 'var(--radius-md)', opacity: bgOpacity,
                                                        zIndex: 1, border: `1px solid ${borderColor}`
                                                    }} />

                                                    {/* Handle de redimensionamiento */}
                                                    <div
                                                        className="gantt-resize-handle"
                                                        onPointerDown={(e) => {
                                                            e.stopPropagation();
                                                            onResizeStart(e, task);
                                                        }}
                                                        onPointerMove={onResizeMove}
                                                        onPointerUp={(e) => onResizeEnd(e, e.pointerId)}
                                                        draggable={false}
                                                        onDragStart={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        style={{
                                                            position: 'absolute', right: 0, top: 0, bottom: 0, width: '16px',
                                                            cursor: 'ew-resize', borderTopRightRadius: '6px', borderBottomRightRadius: '6px',
                                                            zIndex: 100, pointerEvents: 'auto', background: 'transparent'
                                                        }}
                                                    />

                                                    {/* Avatar inline */}
                                                    <div style={{
                                                        width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#444',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                                                        zIndex: 2, marginLeft: 'var(--space-1)', boxShadow: '0 2px 4px rgba(0,0,0,0.5)', position: 'relative', flexShrink: 0
                                                    }}>
                                                        <img src={inlineAvatarUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" loading="lazy" />
                                                    </div>

                                                    {/* Título e ícono */}
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px', zIndex: 2,
                                                        position: 'relative', marginLeft: 'var(--space-2)', overflow: 'visible', whiteSpace: 'nowrap'
                                                    }}>
                                                        {task.is_calendar_event ? (
                                                            task.is_recurring ? <Repeat size={11} color="#fff" style={{ flexShrink: 0 }} /> : <Calendar size={11} color="#fff" style={{ flexShrink: 0 }} />
                                                        ) : task.stage === 'recordatorio' ? (
                                                            <AlarmClock size={11} color="#fff" style={{ flexShrink: 0 }} />
                                                        ) : null}

                                                        <span style={{
                                                            color: '#fff', fontSize: '11px', fontWeight: '500',
                                                            textShadow: '0 1px 2px rgba(0,0,0,0.5)', flexShrink: 0
                                                        }} title={task.title || translate('common.untitled')}>
                                                            {task.title || translate('common.untitled')}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskGanttView;
